import React from 'react'
import * as crashReporter from './crash_reporter.coffee'
import CrashScreen from './crash_screen.coffee'
import {beginRecovery, clearRecovery, performRecovery, clearAndRefresh, REFRESH, DASHBOARD, STOP} from './crash_recovery.coffee'

# HOW LONG THE SCREEN STAYS UP BEFORE IT ACTS — card 6ab166a3a4d698cda2b1fa90.
#
# Long enough to be a screen rather than a flicker (the owner asked for a
# pretty screen, and a screen nobody sees is not one), short enough that a
# child is not waiting. Three and a half seconds is about one bounce and a
# read of the headline.
MIN_SCREEN_MS = 3500

# THE REPORT GOES FIRST. The card's constraint: "The CrashLog report must still
# be sent before the automatic refresh fires." crash_reporter posts with
# `keepalive: true`, so the request survives a navigation on its own — but
# `keepalive` is a browser promise, not ours, and the row is the only record
# that this crash happened at all. So we wait for the server's answer.
#
# CAPPED, because the thing that broke may be the network. A report that has
# not come back in five seconds is not going to rescue anybody by being waited
# on longer, and the refresh is what the family is here for.
REPORT_BUDGET_MS = 5000

# STILL UP AFTER THIS MEANS THE RECOVERY WORKED. The escalation
# (refresh -> dashboard -> stop) is only meaningful while the steps are part of
# one incident. Once the app has rendered and stayed rendered, the next crash
# is a new incident and starts again at `refresh`. Without this the guard's
# own expiry window would make an unrelated crash half a minute later skip
# straight to the dashboard.
HEALTHY_MS = 15000

delay = (ms) -> new Promise (resolve) -> setTimeout resolve, ms

export default class ErrorBoundary extends React.Component
  constructor: (props) ->
    super props
    @state =
      hasError: false
      error: null
      phase: null

  # Nothing has thrown by the time this runs, so the children mounted. If they
  # are still mounted HEALTHY_MS from now, whatever recovery got us here worked.
  componentDidMount: ->
    @healthyTimer = setTimeout (=> clearRecovery window), HEALTHY_MS

  componentWillUnmount: ->
    clearTimeout @healthyTimer if @healthyTimer?
    clearTimeout @recoveryTimer if @recoveryTimer?

  logError: (error) ->
    if window.app?.trackError?
      window.app.trackError(error)
    else
      console.error "Error without app!"
      console.dir error

  # The crash reports ITSELF (card 6aad7bb96e6a1e18a090353b): one CrashLog row
  # with the error, the component stack and everything the reporter knows.
  #
  # RETURNS THE PROMISE since card 6ab166a3a4d698cda2b1fa90 — the recovery
  # waits on it, so "we reported it" has to be something the caller can await
  # rather than a side effect.
  reportCrash: (error, info, phase) ->
    kind = if error?.name is 'ChunkLoadError' then 'chunkload' else 'boundary'
    # WHICH chunk, and how hard we tried — card 6aad9e4dbb8767fbeba89f2c.
    # lazy_route stamps these on the error it finally gives up on, so the row
    # says whether the retries are rescuing anyone rather than leaving us to
    # guess from the absence of reports. Absent for every other kind of crash.
    context = {}
    context.chunk = error.chunkName if error?.chunkName?
    context.chunkAttempts = error.chunkAttempts if error?.chunkAttempts?
    # The step this crash is about to take rides along on the row, so the
    # CrashLog says whether a family was refreshed, sent to the dashboard or
    # left on the screen — which is the difference between one bad moment and
    # somebody who could not get back in.
    # Passed in rather than read off @state: setState is asynchronous, and the
    # row would otherwise carry the PREVIOUS crash's step, or nothing at all.
    context.recovery = phase if phase?
    crashReporter.report(kind, error, {componentStack: info?.componentStack, context: context})
      .then (ok) =>
        @setState {reported: ok}
        ok
      .catch =>
        @setState {reported: false}
        false

  componentDidCatch: (error, info) ->
    # note: the actual error tends NOT to match the actual root cause, which leads
    #     : to a lot of unnecessary and misleading logging.
    realError = info?.componentStack || error

    # The app did not stay up, so the healthy-boot clear must not fire and
    # erase the escalation we are in the middle of.
    clearTimeout @healthyTimer if @healthyTimer?

    # DECIDED AND RECORDED HERE, BEFORE ANYTHING IS SHOWN OR SENT. The phase is
    # what the screen says and what the recovery does, and writing the guard
    # first is what keeps a crash loop from spending the same step twice
    # (crash_recovery.coffee says why at length).
    phase = beginRecovery window

    @setState
      hasError: true
      error: error
      realError: realError
      reported: null
      phase: phase

    # log the error
    @logError(realError)

    reported = @reportCrash(error, info, phase)
    return if phase is STOP

    startedAt = Date.now()
    Promise.race([reported, delay(REPORT_BUDGET_MS)])
      .catch(-> null)
      .then =>
        remaining = Math.max 0, MIN_SCREEN_MS - (Date.now() - startedAt)
        @recoveryTimer = setTimeout (->
          performRecovery phase, window, crashReporter.getAppName()
        ), remaining

  refreshNow: =>
    clearTimeout @recoveryTimer if @recoveryTimer?
    clearRecovery window
    try
      window.location.reload()
    catch e
      null

  clearAndRefreshNow: =>
    clearTimeout @recoveryTimer if @recoveryTimer?
    # The shell is passed for the same reason performRecovery is given it: the
    # way out lands on the shell we crashed in, not always /app. It is only the
    # FALLBACK here — crash_recovery reads the path first, because on a boot
    # crash the reporter has not been installed yet and still says 'app'
    # (card 6ab35fca4f3ebf539a382155).
    clearAndRefresh window, crashReporter.getAppName()

  # HONEST COPY, KEPT — card 6aadb69ed60dadb9b2befafa. The sentence tracks what
  # the reporter actually managed to do, because the screen used to promise a
  # report that the server's flood guard had dropped — and the guard trips
  # exactly when a device is crash-looping, which is when we most need the
  # family to write in.
  #
  # The card that rewrote this screen (6ab166a3a4d698cda2b1fa90) took the
  # support essay off it, not this. It is shown only once the escalation has
  # STOPPED: while we are still refreshing, the screen is a moment long and the
  # family has nothing to do with the sentence; when we have stopped, they are
  # sitting in front of it and whether we have the crash is the one useful
  # thing left to say.
  reportLine: ->
    switch @state.reported
      when true then 'This error has been reported to our team.'
      when false then 'We could not report this error automatically.'
      else 'We are reporting this error to our team.'

  render: ->
    return @props.children unless @state.hasError

    # A CHUNK THAT WOULD NOT LOAD TAKES THE SAME PATH — the card left this open
    # ("ChunkLoadError currently has its own copy (connection trouble); decide
    # on the card whether it takes the same path") and the answer is yes, with
    # its own headline.
    #
    # A reload is not a guess for this one, it is the fix: a chunk 404s when the
    # bundle this page is running was replaced under it by a client point, and
    # the reload fetches the new index. lazy_route already spends a chunk-level
    # retry before the error ever reaches here (card 6aad9e4dbb8767fbeba89f2c),
    # so by this point a page-level reload is the next thing to try and the
    # escalation stops it from becoming a loop. What it keeps is the connection
    # sentence, because that is the other half of why a chunk does not arrive.
    chunk = @state.error?.name is 'ChunkLoadError'
    headline = if chunk then 'We are having trouble reaching our servers.' else undefined
    subline =
      if chunk and @state.phase isnt STOP
        'Please check your internet connection — we will try again in a moment...'
      else if chunk
        'Please check your internet connection, then try the button below.'
      else
        undefined

    <CrashScreen
      phase={@state.phase ? STOP}
      headline={headline}
      subline={subline}
      note={if (@state.phase ? STOP) is STOP then @reportLine() else undefined}
      onClearAndRefresh={@clearAndRefreshNow}
    />
